import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import DownloadIcon from '@material-design-icons/svg/filled/download.svg?react';
import ImageIcon from '@material-design-icons/svg/filled/image.svg?react';
import InsertDriveFileIcon from '@material-design-icons/svg/filled/insert_drive_file.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatFileSize } from '../utils/stringHelpers';

export interface MediaItem {
  purpose: string;
  uri: string;
  mime: string;
  size?: number;
  sha256?: string;
  width?: number;
  height?: number;
}

export interface FileItem extends MediaItem {
  purpose: string;
  uri: string;
  mime: string;
  size?: number;
}

interface MetadataMediaCarouselProps {
  media?: MediaItem[];
  files?: FileItem[];
  className?: string;
}

const EMPTY_MEDIA: MediaItem[] = [];
const EMPTY_FILES: FileItem[] = [];

export function MetadataMediaCarousel({
  media = EMPTY_MEDIA,
  files = EMPTY_FILES,
  className = '',
}: MetadataMediaCarouselProps) {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const allItems = [...media, ...files];

  if (allItems.length === 0) {
    return null;
  }

  const getFileIcon = (item: MediaItem | FileItem) => {
    if (item.mime?.startsWith('image/')) {
      return <ImageIcon className="size-6 fill-current text-blue-400" />;
    }
    if (item.mime?.includes('android') || item.purpose === 'install') {
      return (
        <InsertDriveFileIcon className="size-6 fill-current text-green-400" />
      );
    }
    return (
      <InsertDriveFileIcon className="size-6 fill-current text-neutral-400" />
    );
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    toast.success('URL copied to clipboard');

    setTimeout(() => {
      setCopiedUrl(null);
    }, 2000);
  };

  return (
    <div className={`${className}`}>
      <div className="grid grid-cols-1 gap-3">
        {allItems.map((item, index) => (
          <div
            key={`${item.uri}-${index}`}
            className="bg-neutral-900/50 rounded-lg p-3 border border-neutral-700/50"
          >
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                {item.mime?.startsWith('image/') ? (
                  <div className="relative size-16 rounded overflow-hidden bg-neutral-800">
                    <img
                      src={item.uri}
                      alt={item.purpose}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="size-16 bg-neutral-800 rounded flex items-center justify-center">
                    {getFileIcon(item)}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-medium text-white capitalize">
                    {item.purpose.replace(/_/g, ' ')}
                  </h4>
                  <div className="flex gap-1 flex-shrink-0">
                    <a
                      href={item.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-neutral-400 hover:text-white transition-colors"
                      title="Open in new tab"
                    >
                      <OpenInNewIcon className="size-4 fill-current" />
                    </a>
                    <a
                      href={item.uri}
                      download
                      className="p-1 text-neutral-400 hover:text-white transition-colors"
                      title="Download"
                    >
                      <DownloadIcon className="size-4 fill-current" />
                    </a>
                  </div>
                </div>

                <div className="text-xs text-neutral-400">
                  <span className="text-neutral-300 break-all">
                    {item.mime}
                  </span>
                </div>

                <div className="text-xs text-neutral-400">
                  <span className="text-neutral-300">
                    {formatFileSize(item.size)}
                  </span>
                  {item.width && item.height && (
                    <>
                      <span className="mx-1.5">•</span>
                      <span className="text-neutral-300">
                        {item.width} × {item.height}
                      </span>
                    </>
                  )}
                </div>

                {item.sha256 && (
                  <div className="text-xs">
                    <span className="text-neutral-400">SHA256: </span>
                    <span className="text-neutral-300 font-mono">
                      {item.sha256.substring(0, 8)}...
                    </span>
                  </div>
                )}

                <div className="flex items-start gap-2 min-w-0 text-xs">
                  <span className="text-neutral-400 flex-shrink-0">URL:</span>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(item.uri)}
                    className="text-teal-400 hover:text-teal-300 text-left group/url transition-colors min-w-0 overflow-hidden flex-1"
                    title="Click to copy URL"
                  >
                    <div className="flex items-center gap-1">
                      <span className="truncate">{item.uri}</span>
                      <ContentCopyIcon
                        className={`w-3 h-3 fill-current flex-shrink-0 transition-opacity ${
                          copiedUrl === item.uri
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/url:opacity-100'
                        }`}
                      />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
